import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { ToolPageLayout, type ToolPageContent } from './ToolPageLayout';
import { PROBED_SELECTOR_COUNT } from '../../../lib/dkimSelectors';

interface DomainCheckResult {
  domain: string;
  score: number;
  spf: { status: 'present' | 'missing' | 'multiple' | 'invalid'; policy: string | null; record: string | null };
  dkim: { status: 'present' | 'missing'; selectorsFound: string[] };
  dmarc: { status: 'present' | 'missing' | 'invalid'; policy: string | null; pct: number | null; record: string | null };
  mx: string[];
}

type Variant = 'spf' | 'dkim' | 'dmarc' | 'all';

const STATUS_TONE: Record<string, string> = {
  present: 'text-allow border-allow/30 bg-allow/10',
  missing: 'text-review border-review/30 bg-review/10',
  multiple: 'text-verify border-verify/30 bg-verify/10',
  invalid: 'text-review border-review/30 bg-review/10',
};

/**
 * One component, four routes. The interactive lookup (form + ResultCards) is
 * shared across the SPF / DKIM / DMARC / domain-health variants — only the
 * marketing chrome (title, intro, educational copy, FAQ, related tools, CTA,
 * accent, icon, slug) differs per variant, supplied by VARIANT_CONTENT. The
 * shared body is injected into ToolPageLayout's `body` slot; the layout owns
 * SEO + JSON-LD so this component no longer calls useSeo itself.
 */
type VariantContent = Omit<ToolPageContent, 'body'> & { highlight: 'spf' | 'dkim' | 'dmarc' | 'all' };

const VARIANT_CONTENT: Record<Variant, VariantContent> = {
  spf: {
    slug: 'spf-checker',
    title: 'Free SPF checker',
    appName: 'SPF record checker',
    tagline: 'Pull and parse any domain\'s SPF record + policy in one lookup.',
    eyebrow: 'Free tool · No signup · DNS-only',
    description:
      'Free SPF record checker. Verifies your domain\'s SPF posture, policy (-all / ~all / ?all), and surfaces missing or invalid records.',
    icon: <Icon.ShieldCheck />,
    accent: 'verify',
    highlight: 'spf',
    intro:
      'Look up your domain\'s SPF record and parse the policy. SPF authorises which servers may send mail using your domain in the envelope-from — mailbox providers downrank or reject messages whose sending IP isn\'t covered.',
    educational: {
      title: 'What this checker reads',
      paragraphs: [
        'It queries the TXT records at your domain root, finds the one beginning v=spf1, and reports whether SPF is present, valid, missing or duplicated — plus the trailing qualifier you are publishing.',
        'Two conditions void a record entirely and both look fine in a DNS panel: publishing more than one v=spf1 record, and exceeding the 10-DNS-lookup limit once every include is expanded. Either returns permerror, which receivers treat as no SPF at all.',
      ],
    },
    faq: [
      { q: 'What does this SPF checker actually look up?', a: 'It queries the TXT records at your domain root, finds the one beginning v=spf1, and parses it — reporting whether SPF is present, valid, missing, or duplicated, plus the trailing policy qualifier (-all, ~all, or ?all).' },
      { q: 'What\'s the difference between -all, ~all and ?all?', a: '-all is a hard fail (reject unauthorised senders), ~all is a soft fail (accept but mark suspicious), and ?all is neutral (no opinion). Most domains should run ~all while ramping and tighten to -all once they\'re confident every legitimate sender is listed.' },
      { q: 'Why does it say my record is invalid when I have one?', a: 'The two usual causes are publishing more than one v=spf1 TXT record (RFC 7208 permits exactly one — multiple records is an automatic permerror) and exceeding the 10-DNS-lookup limit by chaining too many include: mechanisms. Either condition fails the whole record.' },
      { q: 'Does passing SPF mean my email is authenticated?', a: 'Partly. SPF authenticates the envelope-from domain, but it breaks on forwarding and doesn\'t cover the visible From: header on its own. You need DKIM and DMARC alignment on top of SPF for a complete, forwarding-resilient setup.' },
      { q: 'I don\'t have an SPF record yet — what do I do?', a: 'Build one with the free SPF generator, publish it as a TXT record at your domain root, wait for DNS to propagate, then re-run this checker to confirm it resolves and the policy is what you intended.' },
      { q: 'How often should I re-check SPF?', a: 'Any time you add or remove a sending service, and as part of routine pre-send QA before a big campaign. SPF is set-and-forget until someone changes your stack — and that change is exactly when it silently breaks.' },
    ],
    relatedTools: [
      { slug: 'spf-generator', title: 'SPF record generator', tagline: 'Build a valid v=spf1 record from your sending services.' },
      { slug: 'dkim-checker', title: 'DKIM checker', tagline: 'Probe the common DKIM selectors and confirm signing is live.' },
      { slug: 'dmarc-checker', title: 'DMARC checker', tagline: 'Read your DMARC policy + pct% and flag weak posture.' },
    ],
    ctaTitle: 'Want SPF verified on every domain you send from?',
    ctaBody:
      'SieveGuard\'s Domain Health add-on re-runs this SPF check continuously across every sending domain you onboard, alerts you when a record changes or breaks the 10-lookup limit, and surfaces the result as a per-record badge.',
  },
  dkim: {
    slug: 'dkim-checker',
    title: 'Free DKIM checker',
    appName: 'DKIM record checker',
    tagline: `Probe ${PROBED_SELECTOR_COUNT} known DKIM selectors and confirm signing is live.`,
    eyebrow: 'Free tool · No signup · DNS-only',
    description: `Free DKIM checker. Probes ${PROBED_SELECTOR_COUNT} known DKIM selectors in parallel and reports which one(s) publish a signing key.`,
    icon: <Icon.Key />,
    accent: 'verify',
    highlight: 'dkim',
    // Counts come from PROBED_SELECTOR_COUNT, never a literal. This copy claimed "the 14 most
    // common" for as long as it existed while the prober ran 36, and enumerated six selectors
    // (mailgun, k2, k3, m1, m2, m3, mx) it has never contained — undersold AND inaccurate.
    intro: (
      <>
        Probe {PROBED_SELECTOR_COUNT} known DKIM selectors (google, selector1/2, k1, s1/s2,
        amazonses, brevo1/2, protonmail…) in parallel. If one publishes a key, DKIM signing is
        active — and both Gmail and Outlook treat unsigned mail with deep suspicion. Not sure what a
        selector even is?{' '}
        <Link to="/learn/dkim-selector" className="text-accent underline underline-offset-4">
          Start here
        </Link>
        .
      </>
    ),
    educational: {
      title: 'What this checker can and cannot find',
      paragraphs: [
        'DNS has no way to list the DKIM selectors a domain publishes, so this probes a curated list and reports every key it finds. A result means signing is live for that selector.',
        'Nothing found does not always mean DKIM is missing. Some providers mint a random per-account selector that no probe list can guess — Amazon SES BYODKIM among them. In that case read the s= tag off a message you actually sent.',
      ],
    },
    faq: [
      { q: 'Why do I need a selector to check DKIM?', a: `DKIM public keys are published under a selector you choose (or your provider chooses), at selector._domainkey.yourdomain.com. There's no DNS mechanism to list every selector for a domain, so this tool probes the ${PROBED_SELECTOR_COUNT} selectors we track — covering Google Workspace, Microsoft 365, Mailchimp, SendGrid, Amazon SES, Brevo, Zoho, Proton and the generic self-hosted defaults — and reports any that resolve.` },
      { q: 'It found nothing — does that mean DKIM is broken?', a: `Not necessarily. It means none of the ${PROBED_SELECTOR_COUNT} selectors we probe published a key. Some providers mint a random per-account selector that no probe list can guess (Amazon SES BYODKIM does this). Read the s= tag off a message you actually sent, or check your provider's dashboard for the exact name.` },
      { q: 'How is DKIM different from SPF?', a: 'SPF authorises sending IPs and breaks on forwarding; DKIM cryptographically signs the message itself and survives forwarding. They\'re complementary — DMARC requires at least one of them to pass and align with your visible From: domain.' },
      { q: 'Can I have more than one DKIM selector?', a: 'Yes, and you usually should. Each sending service publishes its own selector, so a domain that sends from Google Workspace plus a transactional provider will legitimately have two or more live selectors. This checker reports every common one it finds.' },
      { q: 'Does a longer key matter?', a: 'Use 2048-bit keys where your provider supports them — 1024-bit is still widely accepted but increasingly considered weak. The key length is set when your ESP generates the record, not something you change in this checker.' },
      { q: 'When should I run this?', a: 'During domain setup, every time you add a new sending service, and as part of pre-send QA. A missing or mis-published DKIM key is one of the most common reasons mail lands in spam at Gmail and Outlook.' },
    ],
    relatedTools: [
      { slug: 'dkim-generator', title: 'DKIM record generator', tagline: 'Generate a DKIM key pair + the DNS record to publish.' },
      { slug: 'spf-checker', title: 'SPF checker', tagline: 'Pull and parse your domain\'s SPF record and policy.' },
      { slug: 'dmarc-checker', title: 'DMARC checker', tagline: 'Read your DMARC policy + pct% and flag weak posture.' },
    ],
    ctaTitle: 'Keep DKIM signing healthy across every domain',
    ctaBody:
      'SieveGuard\'s Domain Health add-on monitors DKIM signing on every domain you onboard, watching for rotated or removed keys, and surfaces a pass/fail badge alongside SPF and DMARC so nothing silently stops signing.',
  },
  dmarc: {
    slug: 'dmarc-checker',
    title: 'Free DMARC checker',
    appName: 'DMARC record checker',
    tagline: 'Read your DMARC policy + pct% and flag weak none vs strict reject.',
    eyebrow: 'Free tool · No signup · DNS-only',
    description:
      'Free DMARC checker. Reads your DMARC policy + pct%. Flags weak none / quarantine vs strict reject.',
    icon: <Icon.ShieldCheck />,
    accent: 'verify',
    highlight: 'dmarc',
    intro:
      'Pull the DMARC record at _dmarc.yourdomain.com, parse the policy (none / quarantine / reject) and the percentage applied. DMARC tells receivers what to do with mail that fails SPF + DKIM alignment — without it, you cede that decision to whoever spoofs you.',
    educational: {
      title: 'What the policy and pct tell you',
      paragraphs: [
        'This reads the record at _dmarc on your domain and reports the policy and percentage, which is where you sit on the none → quarantine → reject ramp.',
        'p=none is a starting line, not a finish line: it collects reports and offers no protection at all. Anything still sitting there once your senders are authenticated is unfinished work.',
      ],
    },
    faq: [
      { q: 'What does p=none actually do?', a: 'Nothing to delivery — it\'s monitor-only. p=none lets you collect aggregate reports (via the rua tag) showing who sends as your domain, without any enforcement. It\'s the safe first step, but it offers no protection against spoofing until you ramp to quarantine or reject.' },
      { q: 'What\'s the difference between quarantine and reject?', a: 'quarantine tells receivers to treat failing mail as suspicious (typically route to spam); reject tells them to refuse it outright at SMTP time. reject is the goal state — it\'s what stops spoofers from spoofing you at all.' },
      { q: 'What is the pct tag?', a: 'pct controls what fraction of failing mail the policy applies to — pct=25 means only a quarter of failing messages get quarantined or rejected, the rest fall back to the next-weaker action. It\'s a rollout safety valve; you lower the risk while ramping, then remove it (defaulting to 100%) once you\'re confident.' },
      { q: 'Why does my mail still fail DMARC when SPF passes?', a: 'DMARC requires alignment, not just a pass. SPF authenticates the envelope-from domain, which often differs from your visible From: domain when an ESP sends on your behalf — so SPF passes but doesn\'t align. DKIM alignment usually solves this; check that your ESP signs with your domain, not theirs.' },
      { q: 'Do Gmail and Yahoo require DMARC now?', a: 'For bulk senders, yes — both now require at least a published DMARC record (p=none is the minimum) along with SPF and DKIM. Many security-conscious receivers go further and downrank domains still sitting at none.' },
      { q: 'How do I move from none to reject safely?', a: 'Stay at p=none with reporting until your aggregate reports show every legitimate source authenticated and aligned. Then step to quarantine (optionally with a low pct), watch the reports, raise pct to 100%, and finally move to reject. The DMARC generator scaffolds each stage.' },
    ],
    relatedTools: [
      { slug: 'dmarc-generator', title: 'DMARC record generator', tagline: 'Scaffold a DMARC record for each stage of the ramp.' },
      { slug: 'spf-checker', title: 'SPF checker', tagline: 'Pull and parse your domain\'s SPF record and policy.' },
      { slug: 'dkim-checker', title: 'DKIM checker', tagline: 'Probe the common DKIM selectors and confirm signing is live.' },
    ],
    ctaTitle: 'Track your DMARC ramp across every domain',
    ctaBody:
      'SieveGuard\'s Domain Health add-on monitors your DMARC policy on every domain you onboard, flags any drift back toward p=none, and shows enforcement status next to SPF and DKIM so you always know where you sit on the road to reject.',
  },
  all: {
    slug: 'domain-checker',
    title: 'Free domain health check',
    appName: 'Domain health checker',
    tagline: 'Composite SPF / DKIM / DMARC / MX check with a single 0-100 score.',
    eyebrow: 'Free tool · No signup · DNS-only',
    description:
      'Free composite SPF / DKIM / DMARC / MX domain health checker. Returns a 0-100 score + breakdown.',
    icon: <Icon.Radar />,
    accent: 'verify',
    highlight: 'all',
    intro:
      'A composite SPF / DKIM / DMARC / MX check rolled into a single 0-100 score. The same engine that powers the in-app Domain Health add-on, exposed here without an account — one lookup, the full picture.',
    educational: {
      title: 'How the 0–100 score is built',
      paragraphs: [
        'SPF, DKIM, DMARC and MX are each checked and weighted into one composite, so a domain missing DKIM entirely cannot score as well as one where only the DMARC policy is weak.',
        'It is a DNS posture check, not a delivery test. A perfect score confirms your records are right; it says nothing about your sending reputation, which is the other half of whether mail arrives.',
      ],
    },
    faq: [
      { q: 'How is the 0-100 score calculated?', a: 'It\'s a weighted roll-up of the four checks — SPF, DKIM, DMARC, and MX. Present, valid, and appropriately strict records earn full marks; missing or weak ones (a DMARC policy stuck at none, a soft-fail SPF, no DKIM key) cost points. It\'s the same scoring the in-app Domain Health add-on uses.' },
      { q: 'What counts as a good score?', a: 'Roughly: 85+ is a strong, enforced posture; 65-84 is functional but has room to tighten (often DMARC not yet at reject); 40-64 means a pillar is missing or weak; below 40 means authentication is largely absent and your mail is at real risk of spam or rejection.' },
      { q: 'Why include MX records in an authentication check?', a: 'MX records prove your domain can actually receive mail, which matters for two-way deliverability, bounce handling, and DMARC reporting. A domain with great SPF/DKIM/DMARC but no MX is configured to send but can\'t receive — usually a sign the setup is incomplete.' },
      { q: 'Which check should I fix first?', a: 'Follow the breakdown. If SPF or DKIM is missing, fix authentication first — DMARC can\'t enforce without at least one of them aligning. If both pass but DMARC is at none, your next move is to ramp DMARC toward enforcement.' },
      { q: 'Is this the same as a real inbox placement test?', a: 'No. This checks your DNS authentication posture, which is the foundation of deliverability. Whether a specific message lands in the inbox also depends on content and reputation — for that, use the email spam checker, or run a real seed-list placement test inside SieveGuard.' },
      { q: 'How often should I run it?', a: 'At domain setup, as pre-send QA before important campaigns, and on a recurring schedule for monitoring. DNS drifts; a clean score today doesn\'t guarantee a clean score after the next stack change.' },
    ],
    relatedTools: [
      { slug: 'spf-checker', title: 'SPF checker', tagline: 'Pull and parse your domain\'s SPF record and policy.' },
      { slug: 'dkim-checker', title: 'DKIM checker', tagline: 'Probe the common DKIM selectors and confirm signing is live.' },
      { slug: 'dmarc-checker', title: 'DMARC checker', tagline: 'Read your DMARC policy + pct% and flag weak posture.' },
    ],
    ctaTitle: 'Want this score on every domain you ingest?',
    ctaBody:
      'SieveGuard\'s Domain Health add-on runs this exact composite check on every email address you screen, surfaces the score as a per-record badge, and alerts you the moment a record changes. Free during your trial.',
  },
};

export function DomainCheckPage({ variant }: { variant: Variant }) {
  const variantContent = VARIANT_CONTENT[variant];

  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const gate = useTurnstileGate();
  const check = useMutation<DomainCheckResult, ApiError, string>({
    mutationFn: (d) =>
      api<DomainCheckResult>(`/public/tools/domain/${encodeURIComponent(d)}`, {
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setError(null);
      setRateLimited(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 429) {
        setRateLimited(true);
        setError(null);
      } else {
        setError(e instanceof Error ? e.message : 'Lookup failed.');
        setRateLimited(false);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!domain.trim()) return;
    check.mutate(domain.trim());
  }

  const { highlight, ...rest } = variantContent;

  const content: ToolPageContent = {
    ...rest,
    body: (
      <>
        <Card className="p-6">
          <form onSubmit={onSubmit} {...gate.armProps} className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <Field label="Domain">
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                maxLength={255}
                autoFocus
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              disabled={check.isPending || !domain.trim() || !gate.ready}
              className="h-11 self-end px-5"
            >
              {check.isPending ? 'Checking…' : 'Check'}
            </Button>
          </form>
          {gate.siteKey && (
            <div className="mt-3">
              <DeferredTurnstileWidget gate={gate} />
            </div>
          )}
          {error && <p className="mt-3 text-[13px] text-review">{error}</p>}
          {rateLimited && (
            <p className="mt-3 text-[13px] text-verify">
              You've hit the free daily limit (8 / IP). <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited checks.
            </p>
          )}
        </Card>

        {check.data && (
          <div className="mt-6">
            <ResultCards data={check.data} highlight={highlight} />
          </div>
        )}
      </>
    ),
  };

  return <ToolPageLayout content={content} />;
}

function ResultCards({ data, highlight }: { data: DomainCheckResult; highlight: 'spf' | 'dkim' | 'dmarc' | 'all' }) {
  const tone =
    data.score >= 85 ? 'text-allow' : data.score >= 65 ? 'text-allow' : data.score >= 40 ? 'text-verify' : 'text-review';

  return (
    <section className="mx-auto max-w-3xl">
      {/* Score banner — always shown so visitors get the headline number even when they came in via an SPF-specific landing page. */}
      <Card className="p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-faint">{data.domain}</div>
            <h3 className="mt-1 text-base font-semibold">Composite domain health</h3>
            <p className="mt-1 text-[12.5px] text-muted">Weighted across SPF / DKIM / DMARC / MX.</p>
          </div>
          <div className={cn('font-display text-5xl font-extrabold tabular-nums', tone)}>{data.score}</div>
        </div>
      </Card>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <RecordCard
          title="SPF"
          status={data.spf.status}
          policy={data.spf.policy}
          record={data.spf.record}
          highlighted={highlight === 'spf' || highlight === 'all'}
        />
        <RecordCard
          title="DKIM"
          status={data.dkim.status}
          policy={null}
          record={data.dkim.selectorsFound.length ? data.dkim.selectorsFound.join(', ') : null}
          extraLabel="Selectors"
          highlighted={highlight === 'dkim' || highlight === 'all'}
        />
        <RecordCard
          title="DMARC"
          status={data.dmarc.status}
          policy={data.dmarc.policy ? `${data.dmarc.policy}${data.dmarc.pct != null && data.dmarc.pct !== 100 ? ` · ${data.dmarc.pct}%` : ''}` : null}
          record={data.dmarc.record}
          highlighted={highlight === 'dmarc' || highlight === 'all'}
        />
        <RecordCard
          title="MX records"
          status={data.mx.length > 0 ? 'present' : 'missing'}
          policy={null}
          record={data.mx.length > 0 ? data.mx.join(', ') : null}
          highlighted={highlight === 'all'}
        />
      </div>
    </section>
  );
}

function RecordCard({
  title,
  status,
  policy,
  record,
  extraLabel,
  highlighted,
}: {
  title: string;
  status: string;
  policy: string | null;
  record: string | null;
  extraLabel?: string;
  highlighted: boolean;
}) {
  return (
    <Card className={cn('p-5', highlighted && 'ring-1 ring-accent/40')}>
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className={cn('rounded-md border px-2 py-[2px] font-mono text-[10.5px]', STATUS_TONE[status] ?? '')}>
          {status}
        </span>
      </div>
      {policy && <div className="mt-2 font-mono text-[12px] text-allow">{policy}</div>}
      {record && (
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-wider text-faint">
            {extraLabel ?? 'Record'}
          </div>
          <pre className="mt-1 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-2 font-mono text-[11px] text-muted">
            {record}
          </pre>
        </div>
      )}
    </Card>
  );
}

// Default export required by React Router framework-mode route modules.
export default DomainCheckPage;
